import React, { useCallback, useEffect, useMemo, useState } from "react";
import DropDown from "../../../../../components/forms/dropdown";
import DisplayTexts from "../display-texts";
import SwitchComponent from "../../../../../components/switch";
import { LANGUAGES, DEFAULT_LANGUAGE } from "../../../../../consts/languages";

const getDividerCss = (showTopDivider, showBottomDivider) => {
  let dividerCss = 'border-gray-100';

  if (showTopDivider) {
    dividerCss = `${dividerCss} border-t-2 pt-4 mt-4`
  }

  if (showBottomDivider) {
    dividerCss = `${dividerCss} border-b-2 pb-4 mb-4`
  }

  return dividerCss;
}

/**
 * MultiLang Component
 * - Shows a language selector dropdown
 * - For the selected language, renders DisplayTexts editor
 * - Keeps data in parent state as: { langDisplayTexts: { [lang]: { displayTextOptions: [...] } } }
 */
export default function MultiLang({
  rootClassName = "",
  title = "Multi Language",
  name,
  value = {},
  parameterName,
  responseModifyByAI,
  onChangeData,
  showTopDivider = false,
  showBottomDivider = false
}) {
  const [selectedLang, setSelectedLang] = useState();
  const [enabled, setEnabled] = useState(false);

  // Normalize options for DropDown (supports primitives already)
  const languageOptions = useMemo(() => LANGUAGES.filter(lang => lang !== DEFAULT_LANGUAGE), []);

  // Read current language data safely
  const currentLangData = useMemo(() => value?.[selectedLang] || {}, [value, selectedLang]);
  const displayTextOptions = currentLangData.displayTextOptions || [];

  // Get list of already added languages
  const addedLanguages = useMemo(() => Object.keys(value || {})
    .filter(lang => lang !== DEFAULT_LANGUAGE && lang !== "null" && lang !== "undefined"), [value]);

  // Auto-enable switch when value contains valid data
  useEffect(() => {
    if (addedLanguages.length > 0) {
      setEnabled(true);
    }
  }, [addedLanguages.length]);

  const handleLangChange = useCallback((event) => {
    const nextLang = event?.target?.value;
    if (nextLang) setSelectedLang(nextLang);
  }, []);

  // When DisplayTexts changes, write back into the map for the current language
  const handleDisplayTextsChange = useCallback((event) => {
    const { name: changedName, value: changedValue } = event.target || {};
    if (changedName !== "displayTextOptions") return;

    const updatedMap = {
      ...(value || {}),
      [selectedLang]: {
        ...(value?.[selectedLang] || {}),
        displayTextOptions: changedValue || []
      }
    };

    onChangeData?.({
      target: {
        name,
        value: updatedMap
      }
    });
  }, [onChangeData, name, value, selectedLang]);

  // Remove a language from the map
  const handleRemoveLanguage = useCallback((langToRemove) => {
    const updatedMap = { ...(value || {}) };
    delete updatedMap[langToRemove];

    // If removing the currently selected language, clear selection
    if (selectedLang === langToRemove) {
      setSelectedLang(undefined);
    }

    onChangeData?.({
      target: {
        name,
        value: updatedMap
      }
    });
  }, [onChangeData, name, value, selectedLang]);

  return (
    <div className={rootClassName}>
      <div className={`flex flex-col gap-3 ${getDividerCss(showTopDivider, showBottomDivider)}`}>
        <div className="flex items-center gap-3 py-2">
          <div className="h-px bg-gray-200 flex-1" />
          <span className="text-sm text-gray-500">{title}</span>
          <SwitchComponent
            enabled={enabled}
            onChange={setEnabled}
          />
          <div className="h-px bg-gray-200 flex-1" />
        </div>

        {enabled && (
          <>
            {/* Display added languages as chips */}
            {addedLanguages.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {addedLanguages.map((lang) => {
                  const isSelected = selectedLang === lang;
                  return (
                    <div
                      key={lang}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${isSelected
                        ? 'bg-primary text-white border border-primary'
                        : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                        }`}
                      onClick={() => setSelectedLang(lang)}
                    >
                      <span className="font-medium">{lang}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveLanguage(lang);
                        }}
                        className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${lang}`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <DropDown
              label="Language"
              name="language"
              placeHolder="Select language"
              options={languageOptions}
              value={selectedLang}
              onChange={handleLangChange}
            />

            <div className="pt-2">
              <DisplayTexts
                displayTopBorder={false}
                parameterName={parameterName}
                displayTextOptionsProps={displayTextOptions}
                responseModifyByAI={responseModifyByAI}
                onChangeData={handleDisplayTextsChange}
                showResponseModifyByAI={false}
                showGenerateTextByAI={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
